/**
 * EVBreakdownSection.tsx
 * 
 * Componente expansível que mostra o breakdown detalhado do Enterprise Value
 * para Sétima, 555 Studios e Consolidado.
 * 
 * Sprint 1 - Feature 2: Breakdown do EV
 * 
 * Estrutura:
 * - VP dos FCFF (2025-2029) por empresa
 * - VP do Valor Terminal por empresa  
 * - EV Total = VP FCFF + VP VT
 * - Visualização hierárquica com tooltips dinâmicos
 * 
 * @author GitHub Copilot
 * @version 1.0.0
 * @date 2025-10-20
 */

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  TrendingUp as IconTrendingUp,
  Calculate as IconCalculate,
  AccountBalance as IconBank,
  Business as IconBuilding,
  PhotoCamera as IconPhoto,
  Layers as IconLayers,
} from '@mui/icons-material';
import { ValuationSnapshot } from '../../types/valuation';
import TooltipAuditavel from '../audit/TooltipAuditavel';

// ============================================================================
// TYPES
// ============================================================================

interface EVBreakdownSectionProps {
  /** Snapshot do valuation para mostrar breakdown */
  snapshot: ValuationSnapshot;
  
  /** Cor do cenário (pessimista/base/otimista) */
  color?: string;
  
  /** Label do cenário */
  label?: string;
  
  /** Se deve iniciar expandido */
  defaultExpanded?: boolean;
}

interface CompanyBreakdownProps {
  empresa: 'Sétima' | '555 Studios';
  vpFCFF: number;
  vpTerminal: number;
  evTotal: number;
  wacc: number;
  g: number;
  color: string;
  icon: React.ReactNode;
  periodLabel: string; // ✨ Novo: período dinâmico (ex: "2027-2031")
}

// ============================================================================
// HELPER: Formatação de Moeda
// ============================================================================

const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatPercent = (value: number): string => {
  return `${(value * 100).toFixed(2)}%`;
};

// ============================================================================
// COMPONENT: Company Breakdown Card
// ============================================================================

const CompanyBreakdownCard: React.FC<CompanyBreakdownProps> = ({
  empresa,
  vpFCFF,
  vpTerminal,
  evTotal,
  wacc,
  g,
  color,
  icon,
  periodLabel, // ✨ Recebendo período dinâmico
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const percentVPFCFF = (vpFCFF / evTotal) * 100;
  const percentVPTerminal = (vpTerminal / evTotal) * 100;

  return (
    <Card
      data-testid={`card-ev-${empresa.toLowerCase().replace(' ', '-')}`}
      sx={{
        borderLeft: `4px solid ${color}`,
        height: '100%',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
      }}
    >
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ color }}>{icon}</Box>
            <Typography variant="h6" fontWeight="bold">
              {empresa}
            </Typography>
          </Box>

          <Divider />

          {/* VP FCFF */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              VP dos FCFF ({periodLabel})
            </Typography>
            <TooltipAuditavel
              titulo={`VP dos FCFF - ${empresa}`}
              formula="Σ (FCFF_ano × Fator de Desconto)"
              breakdown={[
                {
                  label: 'Soma dos Fluxos de Caixa Livres',
                  valor: vpFCFF,
                  destaque: true,
                },
                {
                  label: `${percentVPFCFF.toFixed(1)}% do EV`,
                  tipo: 'neutro',
                },
              ]}
              premissas={[
                `WACC: ${formatPercent(wacc)}`,
                `Período: 5 anos (${periodLabel})`,
              ]}
            >
              <Typography
                data-testid={`vp-fcff-${empresa.toLowerCase().replace(' ', '-')}`}
                variant="h5"
                fontWeight="bold"
                sx={{ cursor: 'help', color: color }}
              >
                {formatCurrency(vpFCFF)}
              </Typography>
            </TooltipAuditavel>
            <Typography variant="caption" color="text.secondary">
              {percentVPFCFF.toFixed(1)}% do EV
            </Typography>
          </Box>

          {/* VP Valor Terminal */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              VP do Valor Terminal
            </Typography>
            <TooltipAuditavel
              titulo={`VP do Valor Terminal - ${empresa}`}
              formula="VT = FCFF_2030 / (WACC - g) | VP(VT) = VT / (1 + WACC)^5"
              breakdown={[
                {
                  label: 'Valor Terminal descontado',
                  valor: vpTerminal,
                  destaque: true,
                },
                {
                  label: `${percentVPTerminal.toFixed(1)}% do EV`,
                  tipo: 'neutro',
                },
              ]}
              premissas={[
                `WACC: ${formatPercent(wacc)}`,
                `Crescimento perpétuo (g): ${formatPercent(g)}`,
                `Perpetuidade a partir de 2030`,
              ]}
            >
              <Typography
                data-testid={`vp-terminal-${empresa.toLowerCase().replace(' ', '-')}`}
                variant="h5"
                fontWeight="bold"
                sx={{ cursor: 'help', color: color }}
              >
                {formatCurrency(vpTerminal)}
              </Typography>
            </TooltipAuditavel>
            <Typography variant="caption" color="text.secondary">
              {percentVPTerminal.toFixed(1)}% do EV
            </Typography>
          </Box>

          <Divider />

          {/* EV Total */}
          <Box
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
              padding: 2,
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Enterprise Value
            </Typography>
            <TooltipAuditavel
              titulo={`Enterprise Value - ${empresa}`}
              formula="EV = VP(FCFF) + VP(Valor Terminal)"
              breakdown={[
                {
                  label: `VP dos FCFF (${periodLabel})`,
                  valor: vpFCFF,
                  tipo: 'positivo',
                },
                {
                  label: 'VP do Valor Terminal',
                  valor: vpTerminal,
                  tipo: 'positivo',
                },
                {
                  label: 'Enterprise Value Total',
                  valor: evTotal,
                  destaque: true,
                },
              ]}
              premissas={[
                `Método: DCF (Discounted Cash Flow)`,
                `WACC: ${formatPercent(wacc)}`,
              ]}
              status="success"
            >
              <Typography
                data-testid={`ev-total-${empresa.toLowerCase().replace(' ', '-')}`}
                variant="h4"
                fontWeight="bold"
                sx={{ cursor: 'help', color: color }}
              >
                {formatCurrency(evTotal)}
              </Typography>
            </TooltipAuditavel>
          </Box>

          {/* Premissas */}
          {!isMobile && (
            <Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  label={`WACC: ${formatPercent(wacc)}`}
                  sx={{ backgroundColor: 'rgba(25, 118, 210, 0.1)' }}
                />
                <Chip
                  size="small"
                  label={`g: ${formatPercent(g)}`}
                  sx={{ backgroundColor: 'rgba(46, 125, 50, 0.1)' }}
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT: EV Breakdown Section
// ============================================================================

const EVBreakdownSection: React.FC<EVBreakdownSectionProps> = ({
  snapshot,
  color = '#1976d2',
  label = 'Cenário Base',
  defaultExpanded = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expanded, setExpanded] = useState(defaultExpanded);

  // Extrair dados
  const setimaData = snapshot.setimaValuation;
  const studios555Data = snapshot.studios555Valuation;
  const consolidado = snapshot.consolidado;

  // Cores por empresa
  const setimaColor = '#1976d2'; // Azul
  const studios555Color = '#f57c00'; // Laranja

  // ✨ CÁLCULO DINÂMICO DO PERÍODO DE PROJEÇÃO
  const projectYears = setimaData.projecoes.map(p => p.year);
  const firstProjectYear = Math.min(...projectYears);
  const lastProjectYear = Math.max(...projectYears);
  const periodLabel = `${firstProjectYear}-${lastProjectYear}`;

  return (
    <Accordion
      data-testid={`accordion-${label?.toLowerCase() || 'breakdown'}`}
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      sx={{
        mt: 3,
        '&:before': {
          display: 'none',
        },
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
          borderLeft: `4px solid ${color}`,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
          },
        }}
      >
        <Box display="flex" alignItems="center" gap={2} width="100%">
          <IconCalculate sx={{ color }} />
          <Box flex={1}>
            <Typography variant="h6" fontWeight="bold">
              Breakdown do Enterprise Value
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Decomposição detalhada: VP FCFF + VP Valor Terminal ({label})
            </Typography>
          </Box>
          {!expanded && (
            <Chip
              label={formatCurrency(consolidado.EV_Total)}
              sx={{
                fontWeight: 'bold',
                backgroundColor: color,
                color: 'white',
              }}
            />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {/* Cards por Empresa */}
          <Grid container spacing={3}>
            {/* Sétima */}
            <Grid item xs={12} md={6}>
              <CompanyBreakdownCard
                empresa="Sétima"
                vpFCFF={setimaData.vp_fcff_total}
                vpTerminal={setimaData.vp_valorTerminal}
                evTotal={setimaData.ev}
                wacc={setimaData.wacc}
                g={setimaData.g}
                color={setimaColor}
                icon={<IconBuilding sx={{ color: setimaColor }} />}
                periodLabel={periodLabel}
              />
            </Grid>

            {/* 555 Studios */}
            <Grid item xs={12} md={6}>
              <CompanyBreakdownCard
                empresa="555 Studios"
                vpFCFF={studios555Data.vp_fcff_total}
                vpTerminal={studios555Data.vp_valorTerminal}
                evTotal={studios555Data.ev}
                wacc={studios555Data.wacc}
                g={studios555Data.g}
                color={studios555Color}
                icon={<IconPhoto sx={{ color: studios555Color }} />}
                periodLabel={periodLabel}
              />
            </Grid>
          </Grid>

          <Divider />

          {/* Consolidado */}
          <Card
            sx={{
              background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
              borderTop: `3px solid ${color}`,
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <IconLayers sx={{ color }} />
                  <Typography variant="h6" fontWeight="bold">
                    Consolidado (Sétima + 555 Studios)
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  {/* VP FCFF Total */}
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        VP FCFF Total
                      </Typography>
                      <TooltipAuditavel
                        titulo="VP dos FCFF Consolidado"
                        formula="Sétima + 555 Studios"
                        breakdown={[
                          {
                            label: 'Sétima',
                            valor: setimaData.vp_fcff_total,
                            tipo: 'positivo',
                          },
                          {
                            label: '555 Studios',
                            valor: studios555Data.vp_fcff_total,
                            tipo: 'positivo',
                          },
                          {
                            label: 'Total',
                            valor: setimaData.vp_fcff_total + studios555Data.vp_fcff_total,
                            destaque: true,
                          },
                        ]}
                      >
                        <Typography variant="h5" fontWeight="bold" sx={{ cursor: 'help' }}>
                          {formatCurrency(setimaData.vp_fcff_total + studios555Data.vp_fcff_total)}
                        </Typography>
                      </TooltipAuditavel>
                    </Box>
                  </Grid>

                  {/* VP Terminal Total */}
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        VP Terminal Total
                      </Typography>
                      <TooltipAuditavel
                        titulo="VP do Valor Terminal Consolidado"
                        formula="Sétima + 555 Studios"
                        breakdown={[
                          {
                            label: 'Sétima',
                            valor: setimaData.vp_valorTerminal,
                            tipo: 'positivo',
                          },
                          {
                            label: '555 Studios',
                            valor: studios555Data.vp_valorTerminal,
                            tipo: 'positivo',
                          },
                          {
                            label: 'Total',
                            valor: setimaData.vp_valorTerminal + studios555Data.vp_valorTerminal,
                            destaque: true,
                          },
                        ]}
                      >
                        <Typography variant="h5" fontWeight="bold" sx={{ cursor: 'help' }}>
                          {formatCurrency(setimaData.vp_valorTerminal + studios555Data.vp_valorTerminal)}
                        </Typography>
                      </TooltipAuditavel>
                    </Box>
                  </Grid>

                  {/* EV Total */}
                  <Grid item xs={12} sm={4}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Enterprise Value
                      </Typography>
                      <TooltipAuditavel
                        titulo="Enterprise Value Consolidado"
                        formula="VP(FCFF) + VP(Terminal)"
                        breakdown={[
                          {
                            label: 'VP dos FCFF',
                            valor: setimaData.vp_fcff_total + studios555Data.vp_fcff_total,
                            tipo: 'positivo',
                          },
                          {
                            label: 'VP do Valor Terminal',
                            valor: setimaData.vp_valorTerminal + studios555Data.vp_valorTerminal,
                            tipo: 'positivo',
                          },
                          {
                            label: 'EV Total',
                            valor: consolidado.EV_Total,
                            destaque: true,
                          },
                        ]}
                        status="success"
                      >
                        <Typography variant="h4" fontWeight="bold" sx={{ cursor: 'help', color: color }}>
                          {formatCurrency(consolidado.EV_Total)}
                        </Typography>
                      </TooltipAuditavel>
                    </Box>
                  </Grid>
                </Grid>

                {/* Info adicional */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                    borderRadius: 1,
                  }}
                >
                  <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconBank fontSize="small" />
                      <Typography variant="body2">
                        <strong>WACC Mix:</strong> {formatPercent(consolidado.WACC_Mix)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconTrendingUp fontSize="small" />
                      <Typography variant="body2">
                        Ponderação baseada no EV de cada empresa
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default EVBreakdownSection;
